import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { format, formatDistanceToNow, isToday, isYesterday, parseISO } from "date-fns"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount)
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num)
}

export function formatPercentage(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`
}

export function formatDate(dateString: string | Date | any) {
  try {
    let date: Date;
    
    if (typeof dateString === 'string') {
      date = new Date(dateString);
    } else if (dateString instanceof Date) {
      date = dateString;
    } else if (dateString && typeof dateString === 'object') {
      if (typeof (dateString as any).toDate === 'function') {
        date = (dateString as any).toDate();
      } else if (typeof (dateString as any)._seconds === 'number') {
        const seconds = (dateString as any)._seconds;
        const nanoseconds = (dateString as any)._nanoseconds || 0;
        date = new Date(seconds * 1000 + nanoseconds / 1000000);
      } else {
        throw new Error('Unrecognized date object format');
      }
    } else {
      throw new Error('Invalid date format');
    }
    
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date value');
    }
    
    // Use UTC methods to avoid timezone shifts for date-only displays
    const year = date.getFullYear();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    
    return `${month} ${day}, ${year}`;
  } catch (error) {
    console.error('Date formatting error:', error);
    return 'Unknown date';
  }
}

export function formatDateTime(date: Date | string | any): string {
  try {
    let dateObj: Date;
    
    // Handle different date formats
    if (!date) {
      return 'N/A';
    }
    
    // Handle Firestore Timestamp objects
    if (date && typeof date === 'object' && date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else if (typeof date === 'string') {
      dateObj = parseISO(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      // Try to convert to date
      dateObj = new Date(date);
    }
    
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }
    
    return format(dateObj, 'MMM d, yyyy h:mm a');
  } catch (error) {
    console.warn('Error formatting datetime:', error, 'Date value:', date);
    return 'N/A';
  }
}

export function formatRelativeTime(date: Date | string | any): string {
  try {
    let dateObj: Date;
    
    // Handle different date formats
    if (!date) {
      return 'N/A';
    }
    
    // Handle Firestore Timestamp objects
    if (date && typeof date === 'object' && date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else if (typeof date === 'string') {
      dateObj = parseISO(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      // Try to convert to date
      dateObj = new Date(date);
    }
    
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }
    
    return formatDistanceToNow(dateObj, { addSuffix: true });
  } catch (error) {
    console.warn('Error formatting relative time:', error, 'Date value:', date);
    return 'N/A';
  }
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

export function generateId(): string {
  return Math.random().toString(36).substr(2, 9)
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function calculatePercentageChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0
  return ((current - previous) / previous) * 100
}

export function getStatusColor(status: string): string {
  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-blue-100 text-blue-800',
    shipped: 'bg-indigo-100 text-indigo-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    payment_failed: 'bg-red-100 text-red-800',
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-gray-100 text-gray-800',
    draft: 'bg-gray-100 text-gray-800',
    published: 'bg-green-100 text-green-800',
    low: 'bg-yellow-100 text-yellow-800',
    critical: 'bg-orange-100 text-orange-800',
    out_of_stock: 'bg-red-100 text-red-800',
  }
  
  return statusColors[status.toLowerCase()] || 'bg-gray-100 text-gray-800'
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

export function sortBy<T>(array: T[], key: keyof T, order: 'asc' | 'desc' = 'asc'): T[] {
  return [...array].sort((a, b) => {
    const aVal = a[key]
    const bVal = b[key]
    
    if (aVal < bVal) return order === 'asc' ? -1 : 1
    if (aVal > bVal) return order === 'asc' ? 1 : -1
    return 0
  })
}

export function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce((groups, item) => {
    const group = String(item[key])
    groups[group] = groups[group] || []
    groups[group].push(item)
    return groups
  }, {} as Record<string, T[]>)
}

export function filterBy<T>(
  array: T[],
  filters: Partial<Record<keyof T, any>>
): T[] {
  return array.filter(item =>
    Object.entries(filters).every(([key, value]) => {
      if (value === undefined || value === null || value === '') return true
      return item[key as keyof T] === value
    })
  )
}

export function searchInObject<T extends Record<string, any>>(
  obj: T,
  searchTerm: string,
  fields: (keyof T)[]
): boolean {
  const term = searchTerm.toLowerCase()
  return fields.some(field => {
    const value = obj[field]
    if (typeof value === 'string') {
      return value.toLowerCase().includes(term)
    }
    if (typeof value === 'number') {
      return value.toString().includes(term)
    }
    return false
  })
}

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function validatePhone(phone: string): boolean {
  const phoneRegex = /^\+?[\d\s\-\(\)]+$/
  return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export function downloadCSV(data: any[], filename: string): void {
  if (data.length === 0) return
  
  const headers = Object.keys(data[0])
  const csvContent = [
    headers.join(','),
    ...data.map(row =>
      headers.map(header => {
        const value = row[header]
        return typeof value === 'string' && value.includes(',')
          ? `"${value}"`
          : value
      }).join(',')
    )
  ].join('\n')
  
  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${filename}.csv`
  link.click()
  window.URL.revokeObjectURL(url)
}

export function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text)
}

export function generateRandomColor(): string {
  const colors = [
    '#00742f', '#ffc901', '#e74c3c', '#3498db', '#9b59b6',
    '#f39c12', '#2ecc71', '#34495e', '#e67e22', '#1abc9c'
  ]
  return colors[Math.floor(Math.random() * colors.length)]
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export function getFileExtension(filename: string): string {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2)
}

export function isImageFile(filename: string): boolean {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']
  const extension = getFileExtension(filename).toLowerCase()
  return imageExtensions.includes(extension)
}

// Rating and review utilities
export interface RatingData {
  averageRating: number;
  totalReviews: number;
  approvedReviews: number; // Note: This represents verified reviews (status: 'verified')
}

export function calculateProductRating(reviews?: any[]): RatingData {
  if (!reviews || reviews.length === 0) {
    return {
      averageRating: 0,
      totalReviews: 0,
      approvedReviews: 0,
    };
  }

  const verifiedReviews = reviews.filter(review => review.status === 'verified');
  const totalReviews = reviews.length;
  
  if (verifiedReviews.length === 0) {
    return {
      averageRating: 0,
      totalReviews,
      approvedReviews: 0,
    };
  }

  const totalRating = verifiedReviews.reduce((sum, review) => sum + review.rating, 0);
  const averageRating = totalRating / verifiedReviews.length;

  return {
    averageRating: Math.round(averageRating * 10) / 10, // Round to 1 decimal place
    totalReviews,
    approvedReviews: verifiedReviews.length,
  };
}

export function formatRating(rating: number): string {
  if (rating === 0) return '0.0';
  return rating % 1 === 0 ? rating.toFixed(1) : rating.toString();
}

export function getRatingStars(rating: number): { full: number; half: boolean; empty: number } {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return {
    full: fullStars,
    half: hasHalfStar,
    empty: emptyStars,
  };
}

export function getPendingReviewsCount(reviews?: any[]): number {
  if (!reviews || reviews.length === 0) {
    return 0;
  }
  
  return reviews.filter(review => review.status === 'pending').length;
}

export function hasPendingReviews(reviews?: any[]): boolean {
  return getPendingReviewsCount(reviews) > 0;
}

/**
 * Format carrier names synchronously (fallback only)
 * This should only be used when Firebase data is not immediately available
 */
export function formatCarrierNameSync(carrierCode: string): string {
  // Simple fallback - just capitalize the code
  return carrierCode.toUpperCase();
}